import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  onSnapshot,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { writeAuditLog } from '@/services/audit'
import { useAuthStore } from './auth'
import type { FlyerData, FlyerVersion } from '@/types'

function flyerVersionsCol(incidentId: string, subjectId: string) {
  return collection(db, 'incidents', incidentId, 'subjects', subjectId, 'flyer_versions')
}

export const useFlyerVersionsStore = defineStore('flyerVersions', () => {
  const versions = ref<FlyerVersion[]>([])
  const loading = ref(false)
  let unsubscribe: Unsubscribe | null = null

  function subscribeToVersions(incidentId: string, subjectId: string) {
    if (unsubscribe) unsubscribe()
    loading.value = true
    const q = query(flyerVersionsCol(incidentId, subjectId), orderBy('version_number', 'asc'))
    unsubscribe = onSnapshot(q, (snap) => {
      versions.value = snap.docs.map((d) => ({
        ...d.data(),
        version_id: d.id,
      } as FlyerVersion))
      loading.value = false
    })
  }

  async function createVersion(
    incidentId: string,
    subjectId: string,
    flyerData: FlyerData,
    pdfUrl: string,
  ): Promise<void> {
    const authStore = useAuthStore()
    const nextNumber = versions.value.length
      ? Math.max(...versions.value.map((v) => v.version_number)) + 1
      : 1

    const createdByName = authStore.userProfile
      ? `${authStore.userProfile.user_first_name} ${authStore.userProfile.user_last_name}`.trim()
      : ''

    await addDoc(flyerVersionsCol(incidentId, subjectId), {
      version_number: nextNumber,
      created_at: serverTimestamp(),
      created_by_uid: authStore.currentUser?.uid ?? '',
      created_by_name: createdByName,
      flyer_data: flyerData,
      pdf_url: pdfUrl,
    })

    await writeAuditLog(incidentId, 'flyer.version_created', 'subject', subjectId, null, {
      version_number: nextNumber,
      pdf_url: pdfUrl,
    })
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    versions.value = []
    loading.value = false
  }

  return { versions, loading, subscribeToVersions, createVersion, teardown }
})
